import { ReactElement, CSSProperties, useMemo } from 'react';

// Nexoya logomark geometry as circles
const LOGO_POINTS = [
  // center
  { cx: 17.9031, cy: 19.789, r: 3.7689 },

  // six medium circles
  { cx: 17.9031, cy: 9.89476, r: 2.8274 },
  { cx: 8.95155, cy: 14.1343, r: 2.8274 },
  { cx: 8.95155, cy: 24.9719, r: 2.8274 },
  { cx: 26.856, cy: 14.1343, r: 2.8274 },
  { cx: 26.856, cy: 24.9719, r: 2.8274 },
  { cx: 17.9031, cy: 29.6824, r: 2.8274 },

  // twelve small circles
  { cx: 33.9231, cy: 19.7888, r: 1.8845 },
  { cx: 17.9031, cy: 1.88447, r: 1.8845 },
  { cx: 33.9231, cy: 29.2111, r: 1.8845 },
  { cx: 1.88447, cy: 19.7888, r: 1.8845 },
  { cx: 17.9031, cy: 37.6921, r: 1.8845 },
  { cx: 1.88447, cy: 10.3654, r: 1.8845 },
  { cx: 9.89445, cy: 6.12544, r: 1.8845 },
  { cx: 25.9131, cy: 6.12544, r: 1.8845 },
  { cx: 9.89445, cy: 33.4514, r: 1.8845 },
  { cx: 25.9131, cy: 33.4514, r: 1.8845 },
  { cx: 33.9231, cy: 10.3654, r: 1.8845 },
  { cx: 1.88447, cy: 29.2111, r: 1.8845 },
];

type Props = {
  size?: string;
  variant?: 'dark' | 'light';
  style?: CSSProperties;
  className?: string;
};

const LogoSpinner = ({ size = '36px', variant = 'dark', style, className }: Props): ReactElement => {
  const fillColor = variant === 'light' ? '#ffffff' : '#0ec76a'; // Nexoya green for dark variant

  const circles = useMemo(
    () =>
      LOGO_POINTS.map((point, index) => {
        // Base opacity: 1.0 for center, 0.4 for medium, 0.2 for small
        let baseOpacity: number;
        if (index === 0) {
          baseOpacity = 1.0;
        } else if (point.r > 2) {
          baseOpacity = 0.4;
        } else {
          baseOpacity = 0.2;
        }

        const delay = index * 0.03;

        return (
          <circle
            key={index}
            cx={point.cx}
            cy={point.cy}
            r={point.r}
            fill={fillColor}
            style={
              {
                '--base-opacity': baseOpacity,
                animationDelay: `${delay.toFixed(2)}s`,
              } as CSSProperties
            }
            className="animate-skeleton-pulse"
          />
        );
      }),
    [fillColor]
  );

  return (
    <svg
      viewBox="0 0 36 40"
      fill="none"
      style={{ width: size, height: size, ...style }}
      className={className}
    >
      {circles}
    </svg>
  );
};

export default LogoSpinner;
